import { STAKE_CONTROL, CONTROL_DIRECTION } from '@constants/bettingConstants'

/**
 * The ONE global stake-control engine. Pure — no redux, no formatting (it never
 * adds thousand separators; display formatting is the card's job via
 * `useRenderValue`). The stake is always a canonical STRING.
 *
 * `bounds` = { min, max, step, maxDecimals }.
 */

// Round to `decimals` and return a clean string (no trailing-zero padding, no
// separators). Also kills float drift from stepping (e.g. 0.1 + 0.2).
const roundTo = (value, decimals) => {
  const factor = 10 ** decimals
  const rounded = Math.round(Number(value) * factor) / factor
  return String(rounded)
}

// Keep only a valid positive decimal string: digits + a single dot + at most
// `maxDecimals` fraction digits. Strips letters, separators, extra dots and
// leading-zero garbage ("00" -> "0", "007" -> "7"). Empty string is allowed
// transiently (never emitted).
export const sanitizeStakeInput = (raw, maxDecimals = 2) => {
  if (raw === null || raw === undefined) return ''
  let s = String(raw).replace(/[^\d.]/g, '')

  // collapse to a single dot (keep the first)
  const firstDot = s.indexOf('.')
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '')
  }

  if (maxDecimals <= 0) {
    // no decimals allowed -> drop the dot and everything after it
    const dot = s.indexOf('.')
    if (dot !== -1) s = s.slice(0, dot)
  } else {
    const [intPart, fracPart] = s.split('.')
    if (fracPart !== undefined) s = `${intPart}.${fracPart.slice(0, maxDecimals)}`
  }

  // strip leading zeros in the integer part ("0" stays, "0.5" stays)
  s = s.replace(/^0+(?=\d)/, '')

  return s
}

// Clamp a numeric value into [min, max]. Returns { value, atMin, atMax } where
// atMin/atMax report whether a limit was hit (used to disable the +/- keys).
export const clampStake = (value, { min, max }) => {
  let v = Number(value)
  if (Number.isNaN(v)) v = min
  let atMin = false
  let atMax = false
  if (v <= min) {
    v = min
    atMin = true
  }
  if (v >= max) {
    v = max
    atMax = true
  }
  return { value: v, atMin, atMax }
}

/**
 * Auto-cashout multiplier — live typing resolver. Same idiom as DIRECT_INPUT:
 * sanitize to `maxDecimals`, allow a transient empty field, snap DOWN when typing
 * above `max`, but do NOT force `min` while typing (a partial "1" / "1.0" is below
 * 1.01) — the min is enforced on blur by `clampAutoCashoutValue`.
 */
export const resolveAutoCashoutInput = (raw, { max, maxDecimals = 2 }) => {
  const clean = sanitizeStakeInput(raw, maxDecimals)
  if (clean === '' || clean === '.') return clean
  if (Number(clean) > max) return roundTo(max, maxDecimals)
  return clean
}

// Clamp a typed auto-cashout value into [min, max] at `maxDecimals` — used on
// blur so the field always settles on an accepted multiplier. Empty is handled
// by the caller (left empty, never forced).
export const clampAutoCashoutValue = (value, { min, max, maxDecimals = 2 }) => {
  let num = Number(value)
  if (Number.isNaN(num) || num < min) num = min
  else if (num > max) num = max
  return roundTo(num, maxDecimals)
}

/**
 * Resolve the next stake from a control interaction.
 * @returns {{ value: string, atMin: boolean, atMax: boolean, rejected: boolean }}
 */
export const resolveStake = ({ type, current, payload = {}, bounds }) => {
  const { min, max, step, maxDecimals = 2 } = bounds

  switch (type) {
    case STAKE_CONTROL.CONTROL_BUTTON: {
      const base = Number(current) || 0
      const next = payload.direction === CONTROL_DIRECTION.DECREMENT ? base - step : base + step
      const { value, atMin, atMax } = clampStake(next, { min, max })
      return { value: roundTo(value, maxDecimals), atMin, atMax, rejected: false }
    }

    case STAKE_CONTROL.QUICK_STAKE: {
      const rejected = Number(payload.value) > max
      const { value, atMin, atMax } = clampStake(payload.value, { min, max })
      return { value: roundTo(value, maxDecimals), atMin, atMax, rejected }
    }

    case STAKE_CONTROL.DIRECT_INPUT:
    default: {
      const clean = sanitizeStakeInput(payload.raw, maxDecimals)
      if (clean === '' || clean === '.') {
        // allow transiting through an empty field — it can never be submitted
        return { value: clean, atMin: true, atMax: false, rejected: false }
      }
      const num = Number(clean)
      // clamp the MAX live (typing above max snaps down); do NOT force min while
      // typing — the BET button + place guard block a sub-min submission.
      if (num > max) {
        return { value: String(max), atMin: false, atMax: true, rejected: true }
      }
      return { value: clean, atMin: num <= min, atMax: num >= max, rejected: false }
    }
  }
}
